import { useEffect, useRef, useState, useCallback } from "react";
import axiosInstance from "../config/axiosInstance";
import { useChatContext } from "../contexts/ChatContext";
import { FiSend, FiMessageSquare } from "react-icons/fi";

const formatTime = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  return d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
};

const formatDate = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "short" });
};

const ChatPage = () => {
  const { socket, connected, setChatOpen } = useChatContext();
  const [conversations, setConversations] = useState([]);
  const [selectedConv, setSelectedConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [typingOther, setTypingOther] = useState(false);
  const [unreadPerConv, setUnreadPerConv] = useState({});
  const messagesEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const selectedConvRef = useRef(null);

  // Keep ref in sync with state so socket listeners always have latest value
  useEffect(() => {
    selectedConvRef.current = selectedConv;
  }, [selectedConv]);

  // Mark chat as open so the context stops counting unread
  useEffect(() => {
    setChatOpen(true);
    return () => setChatOpen(false);
  }, [setChatOpen]);

  // Re-registra os listeners sempre que o socket for (re)criado
  useEffect(() => {
    if (!socket) return;

    const onNewMessage = ({ conversationId, message }) => {
      if (selectedConvRef.current?.id === conversationId) {
        setMessages((prev) => [...prev, message]);
        socket.emit("mark_read", { conversationId });
      } else {
        setUnreadPerConv((prev) => ({
          ...prev,
          [conversationId]: (prev[conversationId] || 0) + 1,
        }));
      }
      setConversations((prev) =>
        prev.map((c) =>
          c.id === conversationId ? { ...c, lastMessageAt: message.createdAt } : c
        )
      );
    };

    const onTyping = ({ conversationId, isTyping }) => {
      if (selectedConvRef.current?.id === conversationId) {
        setTypingOther(isTyping);
      }
    };

    socket.on("new_message", onNewMessage);
    socket.on("user_typing", onTyping);

    return () => {
      socket.off("new_message", onNewMessage);
      socket.off("user_typing", onTyping);
    };
  }, [socket]);

  const fetchConversations = useCallback(async () => {
    try {
      const res = await axiosInstance.get("/chat/conversations");
      setConversations(res.data.conversations || []);
    } catch (err) {
      console.error("[Chat] fetch conversations:", err);
    }
  }, []);

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  const selectConversation = async (conv) => {
    setSelectedConv(conv);
    setMessages([]);
    setLoading(true);
    setTypingOther(false);
    setUnreadPerConv((prev) => ({ ...prev, [conv.id]: 0 }));

    socket?.emit("join_conversation", { conversationId: conv.id });

    try {
      const res = await axiosInstance.get(`/chat/conversations/${conv.id}/messages`);
      setMessages(res.data.messages || []);
    } catch (err) {
      console.error("[Chat] fetch messages:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, typingOther]);

  const handleSend = () => {
    const trimmed = text.trim();
    if (!trimmed || !selectedConv || !socket) return;

    socket.emit("send_message", {
      conversationId: selectedConv.id,
      content: trimmed,
    });
    setText("");
    clearTimeout(typingTimeoutRef.current);
    socket.emit("typing", { conversationId: selectedConv.id, isTyping: false });
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleTyping = (e) => {
    setText(e.target.value);
    if (!selectedConv || !socket) return;

    socket.emit("typing", { conversationId: selectedConv.id, isTyping: true });
    clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => {
      socket?.emit("typing", { conversationId: selectedConv.id, isTyping: false });
    }, 1500);
  };

  return (
    <div style={{ display: "flex", height: "calc(100vh - 120px)", gap: 0, borderRadius: 12, overflow: "hidden", border: "1px solid #e5e7eb" }}>
      {/* Conversation list */}
      <aside style={{ width: 300, borderRight: "1px solid #e5e7eb", background: "#fff", display: "flex", flexDirection: "column" }}>
        <div style={{ padding: "16px 20px", borderBottom: "1px solid #e5e7eb" }}>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: "#111" }}>Conversas</h2>
        </div>
        <div style={{ flex: 1, overflowY: "auto" }}>
          {conversations.length === 0 && (
            <div style={{ padding: 24, color: "#9ca3af", textAlign: "center", fontSize: 14 }}>
              Nenhuma conversa ainda.
            </div>
          )}
          {conversations.map((conv) => (
            <button
              key={conv.id}
              onClick={() => selectConversation(conv)}
              style={{
                display: "flex", alignItems: "center", gap: 12,
                width: "100%", padding: "12px 16px", border: "none",
                borderBottom: "1px solid #f3f4f6",
                background: selectedConv?.id === conv.id ? "#f0f9ff" : "#fff",
                cursor: "pointer", textAlign: "left",
              }}
            >
              <div style={{ width: 40, height: 40, borderRadius: "50%", background: "#e0e7ff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontWeight: 700, color: "#4f46e5", fontSize: 15 }}>
                {conv.customer?.name?.charAt(0)?.toUpperCase() || "?"}
              </div>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontWeight: unreadPerConv[conv.id] > 0 ? 700 : 600, fontSize: 14, color: "#111", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {conv.customer?.name || "Cliente"}
                </div>
                <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>
                  {conv.lastMessageAt ? formatDate(conv.lastMessageAt) : "Sem mensagens"}
                </div>
              </div>
              {unreadPerConv[conv.id] > 0 && (
                <span style={{ position: "relative", display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <span className="chat-badge-ping" style={{ borderRadius: 9 }} />
                  <span className="chat-badge-dot">
                    {unreadPerConv[conv.id] > 9 ? "9+" : unreadPerConv[conv.id]}
                  </span>
                </span>
              )}
            </button>
          ))}
        </div>
      </aside>

      {/* Chat area */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", background: "#f9fafb" }}>
        {!connected && (
          <div style={{ padding: "8px 20px", background: "#fef3c7", color: "#92400e", fontSize: 13, borderBottom: "1px solid #fde68a" }}>
            Sem conexão em tempo real — as mensagens não serão enviadas nem recebidas.
          </div>
        )}
        {!selectedConv ? (
          <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", color: "#9ca3af" }}>
            <FiMessageSquare size={48} style={{ marginBottom: 12, opacity: 0.4 }} />
            <p style={{ fontSize: 15 }}>Selecione uma conversa para começar</p>
          </div>
        ) : (
          <>
            <div style={{ padding: "12px 20px", background: "#fff", borderBottom: "1px solid #e5e7eb", display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{ width: 36, height: 36, borderRadius: "50%", background: "#e0e7ff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, color: "#4f46e5", fontSize: 14 }}>
                {selectedConv.customer?.name?.charAt(0)?.toUpperCase() || "?"}
              </div>
              <div>
                <div style={{ fontWeight: 600, fontSize: 15, color: "#111" }}>{selectedConv.customer?.name || "Cliente"}</div>
                <div style={{ fontSize: 12, color: "#6b7280" }}>{selectedConv.customer?.email || ""}</div>
              </div>
            </div>

            <div style={{ flex: 1, overflowY: "auto", padding: "16px 20px", display: "flex", flexDirection: "column", gap: 8 }}>
              {loading && <div style={{ textAlign: "center", color: "#9ca3af", fontSize: 13 }}>Carregando mensagens…</div>}
              {messages.map((msg) => {
                const isMe = msg.senderType === "COMPANY";
                return (
                  <div key={msg.id} style={{ display: "flex", justifyContent: isMe ? "flex-end" : "flex-start" }}>
                    <div style={{ maxWidth: "65%", padding: "8px 12px", borderRadius: isMe ? "12px 12px 2px 12px" : "12px 12px 12px 2px", background: isMe ? "#4f46e5" : "#fff", color: isMe ? "#fff" : "#111", boxShadow: "0 1px 3px rgba(0,0,0,0.08)", fontSize: 14, lineHeight: 1.5, wordBreak: "break-word" }}>
                      <div>{msg.content}</div>
                      <div style={{ fontSize: 11, marginTop: 4, opacity: 0.7, textAlign: "right" }}>{formatTime(msg.createdAt)}</div>
                    </div>
                  </div>
                );
              })}
              {typingOther && (
                <div style={{ display: "flex", justifyContent: "flex-start" }}>
                  <div style={{ background: "#fff", padding: "8px 14px", borderRadius: "12px 12px 12px 2px", boxShadow: "0 1px 3px rgba(0,0,0,0.08)", fontSize: 13, color: "#6b7280" }}>digitando…</div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            <div style={{ padding: "12px 16px", background: "#fff", borderTop: "1px solid #e5e7eb", display: "flex", gap: 8, alignItems: "flex-end" }}>
              <textarea
                value={text}
                onChange={handleTyping}
                onKeyDown={handleKeyDown}
                placeholder="Digite uma mensagem… (Enter para enviar)"
                rows={1}
                style={{ flex: 1, resize: "none", border: "1px solid #d1d5db", borderRadius: 8, padding: "8px 12px", fontSize: 14, outline: "none", fontFamily: "inherit", lineHeight: 1.5 }}
              />
              <button
                onClick={handleSend}
                disabled={!text.trim()}
                style={{ width: 40, height: 40, borderRadius: 8, background: text.trim() ? "#4f46e5" : "#e5e7eb", color: text.trim() ? "#fff" : "#9ca3af", border: "none", cursor: text.trim() ? "pointer" : "default", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, transition: "background 0.15s" }}
              >
                <FiSend size={16} />
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ChatPage;
